import React, { useState } from 'react';
import {
  Box,
  DefaultCellContent,
  Flex,
  IconButton,
  Modal,
  Table,
  TableColumnType,
  Text,
  useSorting,
} from '@fullstory/components';
import { JourneyMap } from '@fullstory/proto/fs/api/metrics/pagemetrics';
import { FormattedMessage, useIntl } from 'react-intl';
import { EventIdentValues } from '@fullstory/proto/fs/api/segment';
import { int64 } from '@fullstory/proto/google/protobuf';
import {
  DefinedEventRecord,
  NamedElementRecord,
} from '@fullstory/proto/fs/services/search-settings';
import { orderBy } from 'lodash';
import { getLabelForIdent, getTypeForIdent, getSublabelForIdent } from '../util/ident';
import { NodeTypeToString } from '../components/JourneyMap/types';

const ROWS_PER_PAGE = 5;

interface UseOtherEventsModalConfig {
  pivotOthers: JourneyMap.PivotOthers | null | undefined;
  identValuesByHash: { [key: string]: EventIdentValues } | undefined;
  getPageNameById: (pageDefId: number) => string;
  getNamedElement: (elementId: string) => NamedElementRecord | undefined;
  getDefinedEvent: (definedEventId: string) => DefinedEventRecord | undefined;
  sessionCountsByDepth: { [depth: number]: number } | undefined;
  // any custom api events that have been expanded into their field properties
  customVars?: { [key: string]: string };
  // any page api vars that have been expanded into their field properties
  pageVars?: { [key: number]: string };
  // any element api vars that have been expanded into their field properties
  elemVars?: { [key: string]: string };
}

export const useOtherEventsModal = ({
  pivotOthers,
  identValuesByHash,
  getPageNameById,
  getNamedElement,
  getDefinedEvent,
  sessionCountsByDepth,
  customVars,
  pageVars,
  elemVars,
}: UseOtherEventsModalConfig) => {
  const intl = useIntl();
  const otherEventsByDepth = getOtherEventsByDepth(pivotOthers);
  const [showOtherEventsModal, setShowOtherEventsModal] = useState(false);
  const [otherEventsModalDepth, setOtherEventsModalDepth] = useState<number | null>(null);
  const [page, setPage] = useState(1);

  const events =
    (otherEventsModalDepth !== null && otherEventsByDepth[otherEventsModalDepth]) || [];
  const numEvents = events.length;
  const numPages = Math.ceil(numEvents / ROWS_PER_PAGE);

  const pageForward = () => page < numPages && setPage(page + 1);
  const pageBack = () => page > 1 && setPage(page - 1);

  const columns: TableColumnType<JourneyMap.InteractionData>[] = [
    {
      key: 'eventLabel',
      label: intl.formatMessage(
        { defaultMessage: 'Showing top {numEvents, number} events', id: 'HWF2n/' },
        { numEvents },
      ),
      renderCell(event: JourneyMap.InteractionData) {
        const ident =
          event.identHash && identValuesByHash
            ? identValuesByHash[event.identHash.toString()]
            : null;
        if (!ident || !event.identHash) {
          return null;
        }
        const label = getLabelForIdent(ident, getPageNameById, getNamedElement, getDefinedEvent);
        const sublabel = getSublabelForIdent(ident, customVars, pageVars, elemVars);
        return (
          <>
            <DefaultCellContent label={`${label}`} />
            {sublabel && (
              <div style={{ display: 'inline' }}>
                <Text appearance="bodyMedium">
                  {sublabel[0]} <b>{sublabel[1]}</b>
                </Text>
              </div>
            )}
          </>
        );
      },
      width: 320,
    },
    {
      key: 'eventType',
      label: intl.formatMessage({ defaultMessage: 'Event type', id: '9a3g8c' }),
      renderCell(event: JourneyMap.InteractionData) {
        const ident =
          event.identHash && identValuesByHash
            ? identValuesByHash[event.identHash.toString()]
            : null;
        if (!ident || !event.identHash) {
          return null;
        }
        const label = NodeTypeToString[getTypeForIdent(ident)];
        return <DefaultCellContent label={label} />;
      },
      width: 200,
    },
    {
      key: 'sessions',
      label: intl.formatMessage({ defaultMessage: 'Sessions', id: 'Z3EDd9' }),
      renderCell(event: JourneyMap.InteractionData) {
        if (
          !sessionCountsByDepth ||
          otherEventsModalDepth === null ||
          !sessionCountsByDepth[otherEventsModalDepth]
        ) {
          return null;
        }
        const count = int64.toNumber(event.count);
        const countPercent = count / sessionCountsByDepth[otherEventsModalDepth]!;
        const label = intl.formatMessage(
          {
            defaultMessage:
              '{countPercent, number, ::percent .0} · {count, number, ::compact-short}',
            id: '6UU+t3',
          },
          { countPercent, count },
        );
        return <DefaultCellContent label={label} />;
      },
      sortable: true,
    },
  ];

  const sortingProps = useSorting(events, 'sessions', {
    defaultSortDirection: 'desc',
    sortFunction: (entities, _, direction) =>
      orderBy(entities, ({ count }) => int64.toNumber(count), direction),
  });
  const rowStartIdx = (page - 1) * ROWS_PER_PAGE;
  const rowEndIdx = page < numPages ? page * ROWS_PER_PAGE : undefined;
  const rows = sortingProps.rows.slice(rowStartIdx, rowEndIdx);

  const onClose = () => {
    setPage(1);
    setShowOtherEventsModal(false);
  };

  const ModalComponent =
    showOtherEventsModal && numEvents ? (
      <Modal onCloseSignal={onClose}>
        <Modal.Header onCloseSignal={onClose}>
          <FormattedMessage
            defaultMessage="{numEvents} More Events"
            values={{ numEvents }}
            id="j6A5mk"
          />
        </Modal.Header>
        <Modal.Body>
          <Box margin={[16, 8, 8, 8]} minHeight={360}>
            <Box margin={[0, 0, 16, 24]}>
              <Text appearance="bodyLarge">
                <FormattedMessage
                  defaultMessage="Step {stepNum}"
                  values={{ stepNum: (otherEventsModalDepth || 0) + 1 }}
                  id="ljmsRf"
                />
              </Text>
            </Box>
            <Table
              columns={columns}
              rowKeyAccessor="identHash"
              headProps={{
                variant: 'dark',
              }}
              {...sortingProps}
              rows={rows}
            />
          </Box>
          {numPages > 1 && (
            <Flex>
              <Box width="50%">
                <Flex justifyContent="space-between">
                  <IconButton
                    iconName="chevron-left"
                    title={intl.formatMessage({ defaultMessage: 'Previous page', id: 'k9hDFZ' })}
                    disabled={page === 1}
                    onClick={pageBack}
                  />
                  <Text appearance="bodySmall">
                    <FormattedMessage
                      defaultMessage="{page} of {numPages}"
                      values={{ page, numPages }}
                      id="n5yLih"
                    />
                  </Text>
                  <IconButton
                    iconName="chevron-right"
                    title={intl.formatMessage({ defaultMessage: 'Next page', id: 'rBj9Ib' })}
                    disabled={page === numPages}
                    onClick={pageForward}
                  />
                </Flex>
              </Box>
            </Flex>
          )}
        </Modal.Body>
      </Modal>
    ) : null;
  return {
    setShowOtherEventsModal,
    setOtherEventsModalDepth,
    ModalComponent,
  };
};

export function getOtherEventsByDepth(pivotOthers: JourneyMap.PivotOthers | null | undefined): {
  [key: number]: JourneyMap.InteractionData[];
} {
  if (!pivotOthers) {
    return {};
  }
  const othersEventsByDepth: { [key: number]: JourneyMap.InteractionData[] } = {
    0: pivotOthers.top,
  };
  let currentDepth = 1;
  let next = pivotOthers.forward ?? pivotOthers.backward;
  while (next) {
    othersEventsByDepth[currentDepth] = next.top;
    next = next.next;
    currentDepth += 1;
  }
  return othersEventsByDepth;
}
